import { format } from 'date-fns';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUser } from '../context/UserContext.tsx';
import useGoogleCalendar from '../hooks/useGoogleCalendar.ts';
import { createBirthday, updateBirthday } from '../services/api.ts';
import { BirthdayPerson, EditorFocusArea } from './BirthdayList.tsx';
// Material UI imports
import { Delete as DeleteIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Icons

interface AddBirthdayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean, wasSaved?: boolean) => void;
  person?: BirthdayPerson | null;
  initialFocus?: EditorFocusArea;
  onCalendarEventUpdate?: (birthday: any) => Promise<void>;
  onDeleteBirthday?: (id: string) => Promise<void>;
}

const reminderOptions = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
  { value: 30, label: '1 month' }
];

const AddBirthdayDialog = ({ open, onOpenChange, person, initialFocus, onCalendarEventUpdate, onDeleteBirthday }: AddBirthdayDialogProps) => {
  const { user } = useUser();
  
  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      name: '',
      birthday: null as Date | null,
      reminderDays: [] as number[],
      syncWithCalendar: false,
      calendarProvider: 'google', // 'google' or 'apple'
      emoji: '🎂', // Default emoji
    },
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [emojiPickerAnchorEl, setEmojiPickerAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [hasChangedEmoji, setHasChangedEmoji] = useState(false);
  
  // Refs for focusing elements
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const remindersRef = useRef<HTMLDivElement>(null);

  const { 
    userInfo, 
    syncEvent, 
    removeEvent 
  } = useGoogleCalendar();

  // Watch syncWithCalendar value to reset reminders
  const syncWithCalendar = watch('syncWithCalendar');
  
  useEffect(() => {
    // When calendar sync is turned off, clear reminder days
    // When turned on, set default reminder to 7 days if no reminders are set
    if (!syncWithCalendar) {
      setValue('reminderDays', []);
    } else if (syncWithCalendar && watch('reminderDays').length === 0) {
      setValue('reminderDays', [1, 7]);
    }
  }, [syncWithCalendar, setValue, watch]);

  // Update form values when editing an existing person
  useEffect(() => {
    if (person) {
      // Parse the date string to a Date object in local timezone
      let dateObj: Date;
      if (person.birth_date) {
        const [year, month, day] = person.birth_date.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else {
        dateObj = new Date();
      }
      
      const calendarProvider = 'google';
      const hasCalendarSync = !!person.google_calendar_event_id;
      
      reset({
        name: person.name,
        birthday: dateObj,
        reminderDays: hasCalendarSync ? (person.reminder_days || []) : [],
        syncWithCalendar: hasCalendarSync,
        calendarProvider: calendarProvider,
        emoji: person.emoji, // Use existing emoji or default
      });
    } else {
      // Reset form when adding a new person
      // Check if user has auto-sync enabled
      const autoSync = user?.auto_sync_calendar && !!userInfo;
      
      reset({
        name: '',
        birthday: null,
        reminderDays: autoSync ? [1, 7] : [],
        syncWithCalendar: autoSync || false,
        calendarProvider: 'google'
      });
    }
  }, [person, reset, open, user, userInfo]);

  // Handle focusing on specific elements based on initialFocus
  useEffect(() => {
    if (open && initialFocus) {
      // Use a short timeout to ensure DOM elements are ready
      const timeoutId = setTimeout(() => {
        switch (initialFocus) {
          case 'emoji':
            if (emojiButtonRef.current) {
              emojiButtonRef.current.click();
            }
            break;
          case 'name':
            if (nameInputRef.current) {
              nameInputRef.current.focus();
            }
            break;
          case 'date':
            if (datePickerRef.current) {
              const input = datePickerRef.current.querySelector('input');
              if (input) {
                input.focus();
              }
            }
            break;
          case 'reminders':
            if (remindersRef.current) {
              remindersRef.current.scrollIntoView({ behavior: 'smooth' });
              // Can't focus on checkboxes directly, so we just scroll to them
            }
            break;
          default:
            break;
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open, initialFocus]);

  const onSubmit = async (data: any) => {
    setSaveError(null);
    setSyncError(null);
    setIsSaving(true);
    
    try {
      const birthdayData: {
        name: string; 
        birth_date: string; 
        reminder_days: number[];
        emoji: string;
        google_calendar_event_id?: string | null;
        syncWithCalendar?: boolean;
      } = {
        name: data.name,
        birth_date: data.birthday ? format(data.birthday, 'yyyy-MM-dd') : '',
        reminder_days: data.syncWithCalendar ? (data.reminderDays || []) : [],
        emoji: data.emoji,
        syncWithCalendar: data.syncWithCalendar
      };
      
      let savedBirthday;
      
      if (person) {
        // If this is an existing birthday with calendar sync being turned off, unsync from calendar first
        if (person.google_calendar_event_id && !data.syncWithCalendar) {
          setIsSyncing(true);
          try {
            // Remove from Google Calendar
            await removeEvent(person.id);
            
            // Update birthdayData to remove the Google Calendar event ID
            birthdayData.google_calendar_event_id = null;
          } catch (syncErr) {
            console.error('Error unsyncing from calendar:', syncErr);
            setSyncError('Failed to unsync from Google Calendar');
            setIsSaving(false);
            setIsSyncing(false);
            return;
          }
        }
        
        // Update existing birthday
        const response = await updateBirthday(person.id, birthdayData);
        savedBirthday = response;

        // If the birthday has a Google Calendar event already, update it
        if (person.google_calendar_event_id && data.syncWithCalendar && onCalendarEventUpdate) {
          try {
            await onCalendarEventUpdate(savedBirthday);
          } catch (syncErr) {
            console.error('Error updating calendar event:', syncErr);
            setSyncError('Failed to update Google Calendar event');
          }
        }
      } else {
        // Create new birthday - backend will handle calendar sync based on user settings
        const response = await createBirthday(birthdayData);
        savedBirthday = response;
        
        // If user explicitly requested sync, do it immediately for instant feedback
        if (data.syncWithCalendar && data.calendarProvider === 'google') {
          setIsSyncing(true);
          
          try {
            // Prepare birthday event data
            // Parse date in local timezone to avoid UTC conversion
            const [year, month, day] = savedBirthday.birth_date.split('-').map(Number);
            const birthdayEvent = {
              name: savedBirthday.name,
              date: new Date(year, month - 1, day),
              reminderDays: savedBirthday.reminder_days || [],
              appEventId: savedBirthday.id,
              emoji: savedBirthday.emoji
            };
            
            // Sync with Google Calendar (this will check connection and redirect if needed)
            const eventId = await syncEvent(birthdayEvent);
            
            // Update birthday with Google Calendar event ID
            if (eventId) {
              await updateBirthday(savedBirthday.id, {
                ...savedBirthday,
                google_calendar_event_id: eventId
              });
            }
            
          } catch (syncErr) {
            console.error('Error syncing with calendar:', syncErr);
            setSyncError('Failed to sync with Google Calendar');
          } finally {
            setIsSyncing(false);
          }
        }
      }
      
      handleClose(true);
    } catch (error) {
      console.error('Error saving birthday:', error);
      setSaveError('Failed to save birthday');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = (wasSaved = false) => {
    onOpenChange(false, wasSaved);
  };

  const handleReminderChange = (reminderValue: number) => {
    const currentReminders = watch('reminderDays');
    const valueIndex = currentReminders.indexOf(reminderValue);
    
    if (valueIndex === -1) {
      // Add the value if it's not already selected
      setValue('reminderDays', [...currentReminders, reminderValue].sort((a, b) => a - b));
    } else {
      // Remove the value if it's already selected
      setValue('reminderDays', currentReminders.filter(v => v !== reminderValue));
    }
  };

  // Emoji picker functionality
  const handleEmojiPickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiPickerAnchorEl(event.currentTarget);
  };

  const handleEmojiPickerClose = () => {
    setEmojiPickerAnchorEl(null);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setValue('emoji', emojiData.emoji);
    setHasChangedEmoji(true);
    handleEmojiPickerClose();
  };

  // Reset the hasChangedEmoji state when the dialog opens for a new person
  useEffect(() => {
    if (open && !person) {
      setHasChangedEmoji(false);
    } else if (open && person) {
      // If editing an existing person, consider the emoji as already changed
      setHasChangedEmoji(true);
    }
  }, [open, person]);

  const emojiPickerOpen = Boolean(emojiPickerAnchorEl);
  const emojiPickerId = emojiPickerOpen ? 'emoji-picker-popover' : undefined;
  const currentEmoji = watch('emoji');
  
  const handleDelete = async () => {
    if (person && window.confirm('Are you sure you want to delete this birthday?')) {
      if (onDeleteBirthday) {
        // Call the delete function and optimistically close the dialog
        onDeleteBirthday(person.id);
      }
      handleClose(true);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => handleClose(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{person ? 'Edit Birthday' : 'Add Birthday'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}
            
            {syncError && (
              <Alert severity="warning" onClose={() => setSyncError(null)}>
                {syncError}
              </Alert>
            )}
            
            {/* Emoji and Name Section */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ position: 'relative', mt: '8px' }}>
                <Button
                  ref={emojiButtonRef}
                  variant="outlined"
                  onClick={handleEmojiPickerOpen}
                  aria-describedby={emojiPickerId}
                  sx={{ 
                    height: '56px', 
                    width: '56px', 
                    minWidth: '56px',
                    fontSize: '1.5rem',
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(!hasChangedEmoji && {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 8px rgba(25, 118, 210, 0.6)',
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': {
                          boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)',
                        },
                        '70%': {
                          boxShadow: '0 0 0 6px rgba(25, 118, 210, 0)',
                        },
                        '100%': {
                          boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                        },
                      },
                    })
                  }}
                >
                  {currentEmoji}
                </Button>
                <Popover
                  id={emojiPickerId}
                  open={emojiPickerOpen}
                  anchorEl={emojiPickerAnchorEl}
                  onClose={handleEmojiPickerClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                >
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                </Popover>
              </Box>
              
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={nameInputRef}
                    label="Name"
                    placeholder="Friend's name"
                    fullWidth
                    margin="dense"
                    error={!!errors.name}
                    helperText={errors.name?.message?.toString()}
                    InputProps={{
                      sx: { 
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                    inputProps={{
                      style: {
                        height: '100%',
                        boxSizing: 'border-box',
                        paddingTop: '0',
                        paddingBottom: '0'
                      }
                    }}
                  />
                )}
              />
            </Box>

            <Controller
              name="birthday"
              control={control}
              rules={{ required: 'Birthday is required' }}
              render={({ field }) => (
                <Box ref={datePickerRef}>
                  <DatePicker
                    label="Birthday"
                    value={field.value}
                    onChange={(date) => field.onChange(date)}
                    format="dd/MM/yyyy"
                    views={['day', 'month', 'year']}
                    openTo="day"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        error: !!errors.birthday,
                        helperText: errors.birthday?.message?.toString(),
                      },
                    }}
                  />
                </Box>
              )}
            />
            
            {/* Calendar Integration */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Controller
                    name="syncWithCalendar"
                    control={control}
                    render={({ field }) => (
                      <Switch 
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label="Sync with Calendar"
              />
              
              {!watch('syncWithCalendar') && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Reminders are available when synced with Google Calendar
                </Typography>
              )}
            </Box>
            
            {/* Only show Reminders when Calendar sync is enabled */}
            {watch('syncWithCalendar') && (
              <FormControl fullWidth margin="dense" ref={remindersRef}>
                <Typography variant="subtitle1" gutterBottom>
                  Reminders
                </Typography>
                <Controller
                  name="reminderDays"
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      <Box sx={{ width: '50%' }}>
                        {reminderOptions.slice(0, Math.ceil(reminderOptions.length / 2)).map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox 
                                checked={field.value.includes(option.value)}
                                onChange={() => handleReminderChange(option.value)}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </Box>
                      <Box sx={{ width: '50%' }}>
                        {reminderOptions.slice(Math.ceil(reminderOptions.length / 2)).map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox 
                                checked={field.value.includes(option.value)}
                                onChange={() => handleReminderChange(option.value)}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                />
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {person && (
            <Button 
              onClick={handleDelete}
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ marginRight: 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button onClick={() => handleClose(false)}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={isSaving || isSyncing}
          >
            {(isSaving || isSyncing) ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddBirthdayDialog;
